/**
 * Admin → SCIM Tokens
 *
 * Per-tenant bearer tokens for /scim/v2/*. Multiple active tokens are
 * supported for rotation. The plaintext is shown ONCE when issued -
 * after the admin clicks Acknowledge or navigates away, it's lost
 * forever and a new one has to be issued.
 *
 * Format: `scim_tk_<43-char base64url>` - recognisable in logs and
 * incident-response triage.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  issueScimToken,
  listScimTokens,
  revokeScimToken,
  type IssuedScimToken,
  type ScimToken,
} from '@/lib/auth-admin-api';

export function ScimTokens(): JSX.Element {
  const [tokens, setTokens] = useState<ScimToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [justIssued, setJustIssued] = useState<IssuedScimToken | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listScimTokens();
      setTokens(rows);
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function issue(): Promise<void> {
    if (!name.trim()) return;
    setIssuing(true);
    setError(null);
    try {
      const t = await issueScimToken({ name: name.trim() });
      setJustIssued(t);
      setName('');
      await refresh();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    } finally {
      setIssuing(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    if (!confirm('Revoke this token? Any IdP using it will start failing on the next request.')) return;
    try {
      await revokeScimToken(id);
      await refresh();
    } catch (err) {
      setError(String((err as Error)?.message ?? err));
    }
  }

  async function copyPlaintext(): Promise<void> {
    if (!justIssued) return;
    try {
      await navigator.clipboard.writeText(justIssued.plaintext);
      alert('Copied to clipboard. Paste it into your IdP NOW - this is the only chance.');
    } catch {
      alert(`Copy failed. Manually copy: ${justIssued.plaintext}`);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="SCIM Tokens"
        subtitle="Per-tenant bearer tokens for /scim/v2/*. IdPs (Okta, Entra, Google, Ping, JumpCloud) use these to provision and de-provision users."
      />

      {/* One-shot plaintext display */}
      {justIssued && (
        <GlassCard className="border-amber-500/40 bg-amber-500/5">
          <CardHeader>
            <CardTitle>
              <AlertTriangle className="-mt-0.5 mr-2 inline h-4 w-4 text-amber-500" />
              Copy this token NOW
            </CardTitle>
            <CardDescription>
              This is the ONLY time the plaintext will be shown. The server stores only a SHA-256 hash. Paste it into your IdP's SCIM configuration before navigating away.
            </CardDescription>
          </CardHeader>
          <div className="p-5 pt-0">
            <div className="flex items-center gap-2 rounded-md bg-background/60 p-3 font-mono text-xs">
              <code className="flex-1 break-all">{justIssued.plaintext}</code>
              <Button size="sm" variant="outline" onClick={copyPlaintext}>
                <Copy className="-mt-0.5 mr-1 inline h-3 w-3" />
                Copy
              </Button>
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setJustIssued(null)}>
                I've copied it. Dismiss.
              </Button>
            </div>
          </div>
        </GlassCard>
      )}

      <GlassCard>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Issue a token</CardTitle>
              <CardDescription>
                Give the token a name (e.g. &quot;Okta production&quot;) so you can identify it later.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <div className="p-5 pt-0">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-foreground/80">Token name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Okta production"
                className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-xs"
              />
            </div>
            <Button size="sm" onClick={issue} disabled={issuing || !name.trim()}>
              {issuing ? <Loader2 className="-mt-0.5 mr-1 inline h-3 w-3 animate-spin" /> : <Plus className="-mt-0.5 mr-1 inline h-3 w-3" />}
              Issue token
            </Button>
          </div>
          {error && (
            <p className="mt-3 text-xs text-red-500">
              <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
              {error}
            </p>
          )}
        </div>
      </GlassCard>

      <GlassCard className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active + revoked tokens</CardTitle>
              <CardDescription>{tokens.length} tokens total</CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <div className="p-5 pt-0">
          {tokens.length === 0 && !loading && (
            <div className="text-xs text-muted-foreground italic">No SCIM tokens issued yet.</div>
          )}
          <div className="space-y-2">
            {tokens.map((t) => (
              <TokenRow key={t.id} t={t} onRevoke={revoke} />
            ))}
          </div>
        </div>
      </GlassCard>
    </PageContainer>
  );
}

function TokenRow({ t, onRevoke }: { t: ScimToken; onRevoke: (id: string) => void }): JSX.Element {
  const isRevoked = !!t.revokedAt;
  const isExpired = !!t.expiresAt && new Date(t.expiresAt) < new Date();
  const active = !isRevoked && !isExpired;
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <KeyRound className="h-3 w-3 text-foreground/60" />
          <span className="truncate text-sm font-semibold">{t.name}</span>
          {active && (
            <Badge variant="success" className="text-[10px]">
              <CheckCircle2 className="-mt-0.5 mr-1 inline h-3 w-3" />
              Active
            </Badge>
          )}
          {isRevoked && (
            <Badge variant="critical" className="text-[10px]">
              <XCircle className="-mt-0.5 mr-1 inline h-3 w-3" />
              Revoked
            </Badge>
          )}
          {isExpired && !isRevoked && (
            <Badge variant="warning" className="text-[10px]">
              <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
              Expired
            </Badge>
          )}
        </div>
        <p className="mt-1 font-mono text-[10px] text-muted-foreground">
          {t.displayPrefix}… · created {new Date(t.createdAt).toLocaleString()}
          {t.lastUsedAt ? ` · last used ${new Date(t.lastUsedAt).toLocaleString()}` : ' · never used'}
        </p>
      </div>
      {active && (
        <Button size="sm" variant="ghost" onClick={() => onRevoke(t.id)} title="Revoke">
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      )}
    </div>
  );
}
